/* eslint-disable */
import humanizeDuration from 'humanize-duration';
import upperFirst from 'lodash/upperFirst';
import { getCurrentLocale, normalizeLanguage } from './locale_utils';
import i18n from './i18n';

// Define the type for the superclass
type Constructor<T = {}> = new (...args: any[]) => T;

const HumanizeDuration = <T extends Constructor>(superclass: T) =>
  class extends superclass {
    private humanizer: any;

    getHumanizer(options: Record<string, any> = {}) {
      if (this.humanizer) {
        return this.humanizer;
      }

      // Get the current locale and normalize it
      const currentLocale = normalizeLanguage(getCurrentLocale());

      this.humanizer = humanizeDuration.humanizer({
        fallbacks: [currentLocale, 'en'],
        ...options
      });
      return this.humanizer;
    }

    humanizeDuration(valueInMins: number, props?: any) {
      const { smallestUnit, language, units = ['y', 'mo', 'd', 'h', 'm'] } = props || {};

      // Normalize the language if provided, otherwise use the current locale
      const normalizedLanguage = language ? normalizeLanguage(language) : normalizeLanguage(getCurrentLocale());

      if (smallestUnit) {
        // TODO code full logic for different units
        if (valueInMins < 60 * 1000) return '<1min';
      }

      // Use 'shortEn' for English, otherwise use the normalized language
      const displayLanguage = normalizedLanguage ? `short${upperFirst(normalizedLanguage)}` : 'shortEn';

      return this.getHumanizer({ language: normalizedLanguage })(valueInMins, {
        language: displayLanguage,
        languages: {
          shortEn: {
            y: () => 'y',
            mo: () => 'mo',
            w: () => 'w',
            d: () => 'd',
            h: () => 'h',
            m: () => 'm',
            s: () => 's',
            ms: () => 'ms'
          },
          shortEs: {
            y: () => 'y',
            mo: () => 'mo',
            w: () => 'w',
            d: () => 'd',
            h: () => 'h',
            m: () => 'm',
            s: () => 's',
            ms: () => 'ms'
          }
        },
        spacer: '',
        units,
        largest: 1,
        round: true,
        ...props
      });
    }

    humanizeDurationLong(valueInMins: number, props?: any) {
      const { units = ['y', 'mo', 'd', 'h', 'm'], language } = props || {};

      // Normalize the language if provided, otherwise use the current locale
      const normalizedLanguage = language ? normalizeLanguage(language) : normalizeLanguage(getCurrentLocale());

      // Get the duration string from the humanizer
      const durationString = this.getHumanizer({ language: normalizedLanguage })(valueInMins, {
        spacer: ' ',
        units,
        largest: 1,
        round: true,
        ...props
      });

      // Parse the duration string to extract the value and unit
      const match = durationString.match(/(\d+)\s+(\w+)/);
      if (match) {
        const [, value, unit] = match;

        // Map the unit to the corresponding i18n key
        let i18nKey = '';
        if (unit === 'year' || unit === 'years' || unit === 'año' || unit === 'años') {
          i18nKey = value === '1' ? 'time.one_year' : 'time.number_years';
        } else if (unit === 'month' || unit === 'months' || unit === 'mes' || unit === 'meses') {
          i18nKey = value === '1' ? 'time.one_month' : 'time.number_months';
        } else if (unit === 'week' || unit === 'weeks' || unit === 'semana' || unit === 'semanas') {
          i18nKey = value === '1' ? 'time.one_week' : 'time.number_weeks';
        } else if (unit === 'day' || unit === 'days' || unit === 'día' || unit === 'dias') {
          i18nKey = value === '1' ? 'time.one_day' : 'time.number_days';
        } else if (unit === 'hour' || unit === 'hours' || unit === 'hora' || unit === 'horas') {
          i18nKey = value === '1' ? 'time.one_hour' : 'time.number_hours';
        } else if (unit === 'minute' || unit === 'minutes' || unit === 'minuto' || unit === 'minutos') {
          i18nKey = value === '1' ? 'time.one_min' : 'time.number_mins';
        } else if (unit === 'second' || unit === 'seconds' || unit === 'segundo' || unit === 'segundos') {
          i18nKey = value === '1' ? 'time.one_sec' : 'time.number_secs';
        }

        // If we found a matching i18n key, use it to translate the duration
        if (i18nKey) {
          return i18n.t(i18nKey, { n: value, defaultValue: durationString });
        }
      }

      // Fallback to the original duration string if parsing fails
      return durationString;
    }
  };

// Create an empty class to extend from
class EmptyClass {}

// Create the Humanizer class
class Humanizer extends HumanizeDuration(EmptyClass) {}

// Create an instance of the Humanizer class
const humanizer = new Humanizer();

export default humanizer;
